import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch, RootState } from 'store'
import {
  getCoverageModels,
  getCoverageModelVersions,
  saveCoverageModels,
} from 'features/coverage/coverageSlice'
import { fetchCoverageRegistries } from './coverageRegistryFetch'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { ICoverageModelResponse, ISaveCoverageModelShift } from 'types/api/coverageApi.interface'
import { ICoverageEditorRow, ICoverageEditorShift } from 'types/coverage.interface'
import { ISelectValue } from 'constants/globalTypes'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { ToggleButton } from 'components/Input/ToggleButton'
import { PageWrapper, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Head1, TitleText, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { Container, ContainerContent, ContainerHeader } from '../../Accomodation/Accommodation.style'
import {
  COVERAGE_DEPT_MEMBERS,
  coverageCapabilities,
  coverageDepartments,
  coverageLocations,
  coverageRoles,
  coverageShifts,
  coverageSites,
} from './coverageDemoStore'
import {
  ContextBar,
  DeptChips,
  CardStack,
  ShiftCardHeader,
  ShiftTitleGroup,
  ShiftWindowText,
  RowsStack,
  EditorRow,
  CountStepper,
  StepBtn,
  CountValue,
  TimesMark,
  CriticalToggle,
  LocationChip,
  RemoveRowBtn,
  CardFooter,
  CriticalNote,
  MembersWarning,
  SaveBar,
  HintBanner,
  VersionList,
  VersionRow,
  HelperText,
} from './CoverageModels.styled'

const emptyRow = (): ICoverageEditorRow => ({
  count: 1,
  requirementType: 'role',
  requirementId: null,
  critical: false,
  locationNodeId: null,
})

const toEditorRows = (m: ICoverageModelResponse | undefined): ICoverageEditorRow[] =>
  (m?.coverageModelRows || []).map((r) => ({
    count: r.requiredCount,
    requirementType: r.requirementType === 2 ? 'capability' : 'role',
    requirementId: r.requirementType === 2 ? r.capability?.id ?? null : r.role?.id ?? null,
    critical: r.isCritical,
    locationNodeId: r.locationNode?.id ?? null,
  }))

const toWireRows = (rows: ICoverageEditorRow[]): ISaveCoverageModelShift['coverageModelRows'] =>
  rows
    .filter((r) => r.requirementId != null)
    .map((r) => ({
      roleId: r.requirementType === 'role' ? r.requirementId : null,
      capabilityId: r.requirementType === 'capability' ? r.requirementId : null,
      requiredCount: r.count,
      isCritical: r.critical,
      locationNodeId: r.locationNodeId,
    }))

const loadEditor = (models: ICoverageModelResponse[]): { [shiftId: number]: ICoverageEditorShift } => {
  const state: { [shiftId: number]: ICoverageEditorShift } = {}
  coverageShifts().forEach((s) => {
    const m = models.find((x) => x.shift.id === s.shiftId)
    state[s.shiftId] = {
      shiftId: s.shiftId,
      enabled: !!m?.isStaffed,
      rows: toEditorRows(m),
    }
  })
  return state
}

const editorSnapshot = (editor: { [shiftId: number]: ICoverageEditorShift }): { [shiftId: number]: string } => {
  const snap: { [shiftId: number]: string } = {}
  Object.values(editor).forEach((s) => {
    snap[s.shiftId] = JSON.stringify({ enabled: s.enabled, rows: s.enabled ? toWireRows(s.rows) : [] })
  })
  return snap
}

export const CoverageModels = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { models, versions } = useSelector((state: RootState) => state.coverage)
  const [siteId, setSiteId] = useState(() => coverageSites()[0].id)
  const [deptId, setDeptId] = useState(() => coverageDepartments()[0].id)
  const [editor, setEditor] = useState<{ [shiftId: number]: ICoverageEditorShift }>({})
  const [effectiveFrom, setEffectiveFrom] = useState<Date>(new Date())
  const [historyOpen, setHistoryOpen] = useState(false)
  const initialRef = useRef<{ [shiftId: number]: string }>({})

  const todayStr = new Date().toISOString().slice(0, 10)

  const reload = () => {
    dispatch(getCoverageModels({ departmentId: deptId, siteNodeId: siteId, onDate: todayStr }))
    dispatch(getCoverageModelVersions({ departmentId: deptId, siteNodeId: siteId }))
  }

  useEffect(() => {
    fetchCoverageRegistries(dispatch).then(reload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, deptId, siteId])

  useEffect(() => {
    const next = loadEditor(models)
    setEditor(next)
    initialRef.current = editorSnapshot(next)
  }, [models])

  useEffect(() => {
    const sites = coverageSites()
    const depts = coverageDepartments()
    if (sites.length && !sites.some((s) => s.id === siteId)) setSiteId(sites[0].id)
    if (depts.length && !depts.some((d) => d.id === deptId)) setDeptId(depts[0].id)
  }, [models, siteId, deptId])

  const hasAnyModel = useMemo(() => Object.values(editor).some((s) => s.enabled), [editor])
  const deptName = coverageDepartments().find((d) => d.id === deptId)?.name || ''
  const siteName = coverageSites().find((s) => s.id === siteId)?.name || ''
  const members = COVERAGE_DEPT_MEMBERS[`${siteId}-${deptId}`] ?? null
  const siteLocations = coverageLocations().filter((l) => l.siteNodeId === siteId)
  const requirementOptions = [
    ...coverageRoles().map((r) => ({ value: `r-${r.id}`, label: r.name })),
    ...coverageCapabilities().map((c) => ({ value: `c-${c.id}`, label: `${c.name} (capability)` })),
  ]

  const updateShift = (shiftId: number, patch: Partial<ICoverageEditorShift>) =>
    setEditor((prev) => ({ ...prev, [shiftId]: { ...prev[shiftId], ...patch } }))

  const updateRow = (shiftId: number, idx: number, patch: Partial<ICoverageEditorRow>) =>
    setEditor((prev) => {
      const rows = prev[shiftId].rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
      return { ...prev, [shiftId]: { ...prev[shiftId], rows } }
    })

  const removeRow = (shiftId: number, idx: number) => {
    const remaining = editor[shiftId].rows.filter((_, i) => i !== idx)
    if (!remaining.length) {
      dispatch(
        setConfirmModal({
          isOpen: true,
          onSave: async () => updateShift(shiftId, { rows: [], enabled: false }),
          setParams: { type: ModalTypes.BeforeSabe },
          word: 'remove the last row and turn this shift off',
        }),
      )
      return
    }
    updateShift(shiftId, { rows: remaining })
  }

  const findDuplicates = () => {
    const dupes: string[] = []
    Object.values(editor).forEach((s) => {
      if (!s.enabled) return
      const seen: { [k: string]: number } = {}
      s.rows.forEach((r) => {
        if (r.requirementId == null) return
        const key = `${r.requirementType}-${r.requirementId}-${r.locationNodeId ?? 'none'}`
        seen[key] = (seen[key] || 0) + 1
      })
      Object.entries(seen).forEach(([, n]) => {
        if (n > 1) dupes.push(coverageShifts().find((x) => x.shiftId === s.shiftId)?.name || String(s.shiftId))
      })
    })
    return [...new Set(dupes)]
  }

  const mergeDuplicates = () =>
    setEditor((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((k) => {
        const s = next[Number(k)]
        const merged: { [key: string]: ICoverageEditorRow } = {}
        const order: string[] = []
        s.rows.forEach((r) => {
          const key = `${r.requirementType}-${r.requirementId}-${r.locationNodeId ?? 'none'}`
          if (merged[key]) merged[key] = { ...merged[key], count: merged[key].count + r.count, critical: merged[key].critical || r.critical }
          else {
            merged[key] = { ...r }
            order.push(key)
          }
        })
        next[Number(k)] = { ...s, rows: order.map((key) => merged[key]) }
      })
      return next
    })

  const doSave = () => {
    const snap = editorSnapshot(editor)
    const changed: ISaveCoverageModelShift[] = coverageShifts()
      .filter((s) => snap[s.shiftId] !== initialRef.current[s.shiftId])
      .map((s) => ({
        shiftId: s.shiftId,
        coverageModelRows: editor[s.shiftId]?.enabled ? toWireRows(editor[s.shiftId].rows) : [],
      }))
    if (!changed.length) {
      toast.info('No changes to save')
      return
    }
    dispatch(
      saveCoverageModels({
        departmentId: deptId,
        siteNodeId: siteId,
        effectiveFrom: effectiveFrom.toISOString().slice(0, 10),
        coverageModelShifts: changed,
      }),
    ).then((resp: any) => {
      if (resp.meta.requestStatus === 'fulfilled') reload()
    })
  }

  const handleSave = () => {
    const invalid = Object.values(editor).some((s) => s.enabled && s.rows.some((r) => r.requirementId == null))
    if (invalid) {
      dispatch(
        setConfirmModal({
          isOpen: true,
          onSave: async () => undefined,
          setParams: { type: ModalTypes.BeforeSabe },
          word: 'note: rows without a selected requirement will be dropped on save',
        }),
      )
    }
    const dupes = findDuplicates()
    if (dupes.length) {
      dispatch(
        setConfirmModal({
          isOpen: true,
          onSave: async () => {
            mergeDuplicates()
          },
          setParams: { type: ModalTypes.BeforeSabe },
          word: `merge duplicate rows in ${dupes.join(', ')} before saving`,
        }),
      )
      return
    }
    doSave()
  }

  const sortedVersions = useMemo(
    () =>
      [...versions].sort((a, b) =>
        a.effectiveFrom < b.effectiveFrom ? 1 : a.effectiveFrom > b.effectiveFrom ? -1 : a.shift.id - b.shift.id,
      ),
    [versions],
  )

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Coverage Models
          </Head1>
          <Button variant='primary' onClick={handleSave}>
            Save model
          </Button>
        </Flex>
        <OtherText grey>Schedule › Coverage Models · required staffing per department, site and shift</OtherText>

        <ContextBar>
          <HonestSelectInput
            width='200px'
            nomarg
            labelText='Site'
            options={coverageSites().map((s) => ({ value: s.id, label: s.name }))}
            defaultValue={siteId}
            onChange={(v: ISelectValue) => setSiteId(Number(v.value))}
          />
          <DeptChips>
            <TabsContainer>
              {coverageDepartments().map((d) => (
                <Tabs key={d.id} active={deptId === d.id} onClick={() => setDeptId(d.id)}>
                  {d.name}
                </Tabs>
              ))}
            </TabsContainer>
          </DeptChips>
        </ContextBar>

        {!hasAnyModel && (
          <HintBanner>
            No coverage set for {deptName} on {siteName}. Toggle a shift to start.
          </HintBanner>
        )}

        <CardStack>
          {coverageShifts().map((s) => {
            const st = editor[s.shiftId]
            if (!st) return null
            const total = st.rows.reduce((a, r) => a + r.count, 0)
            const hasCritical = st.rows.some((r) => r.critical)
            return (
              <Container key={s.shiftId}>
                <ContainerHeader padding>
                  <ShiftCardHeader>
                    <ShiftTitleGroup>
                      <TitleText>{s.name}</TitleText>
                      <ShiftWindowText>
                        {s.windowFrom}–{s.windowTo}
                      </ShiftWindowText>
                    </ShiftTitleGroup>
                    <ToggleButton
                      label='Department staffs this shift'
                      state={st.enabled}
                      handleChange={() =>
                        updateShift(s.shiftId, {
                          enabled: !st.enabled,
                          rows: !st.enabled && !st.rows.length ? [emptyRow()] : st.rows,
                        })
                      }
                    />
                  </ShiftCardHeader>
                </ContainerHeader>
                {st.enabled && (
                  <ContainerContent noHeader gap='0.75rem'>
                    <RowsStack>
                      {st.rows.map((r, idx) => (
                        <EditorRow key={idx}>
                          <CountStepper>
                            <StepBtn type='button' disabled={r.count <= 1} onClick={() => updateRow(s.shiftId, idx, { count: r.count - 1 })}>
                              −
                            </StepBtn>
                            <CountValue>{r.count}</CountValue>
                            <StepBtn type='button' onClick={() => updateRow(s.shiftId, idx, { count: r.count + 1 })}>
                              +
                            </StepBtn>
                          </CountStepper>
                          <TimesMark>×</TimesMark>
                          <HonestSelectInput
                            width='220px'
                            nomarg
                            placeholder='Role or capability…'
                            options={requirementOptions}
                            defaultValue={r.requirementId != null ? `${r.requirementType === 'role' ? 'r' : 'c'}-${r.requirementId}` : null}
                            onChange={(v: ISelectValue) => {
                              const raw = String(v?.value || '')
                              if (!raw) return
                              updateRow(s.shiftId, idx, {
                                requirementType: raw.startsWith('c-') ? 'capability' : 'role',
                                requirementId: Number(raw.slice(2)),
                              })
                            }}
                          />
                          <CriticalToggle
                            $on={r.critical}
                            title='Unfilled critical positions turn the cell red and escalate at T−4h.'
                          >
                            <input
                              type='checkbox'
                              checked={r.critical}
                              onChange={() => updateRow(s.shiftId, idx, { critical: !r.critical })}
                              style={{ display: 'none' }}
                            />
                            CRITICAL
                          </CriticalToggle>
                          {r.locationNodeId ? (
                            <LocationChip>
                              @ {siteLocations.find((l) => l.id === r.locationNodeId)?.name || 'Location'}
                              <RemoveRowBtn type='button' aria-label='Unpin location' onClick={() => updateRow(s.shiftId, idx, { locationNodeId: null })}>
                                ×
                              </RemoveRowBtn>
                            </LocationChip>
                          ) : (
                            <HonestSelectInput
                              width='170px'
                              nomarg
                              placeholder='@ location (optional)'
                              options={siteLocations.map((l) => ({ value: l.id, label: l.name }))}
                              onChange={(v: ISelectValue) => v?.value && updateRow(s.shiftId, idx, { locationNodeId: Number(v.value) })}
                            />
                          )}
                          <RemoveRowBtn type='button' aria-label='Delete row' onClick={() => removeRow(s.shiftId, idx)}>
                            ✕
                          </RemoveRowBtn>
                        </EditorRow>
                      ))}
                    </RowsStack>
                    <Flex>
                      <Button variant='secondary' size='sm' onClick={() => updateShift(s.shiftId, { rows: [...st.rows, emptyRow()] })}>
                        + Add row
                      </Button>
                    </Flex>
                    <CardFooter>
                      <span>Total required this shift: {total}</span>
                      {hasCritical && <CriticalNote>contains critical positions</CriticalNote>}
                    </CardFooter>
                    {members != null && total > members && (
                      <MembersWarning>
                        Requires {total}, department has {members} members on this site.
                      </MembersWarning>
                    )}
                  </ContainerContent>
                )}
              </Container>
            )
          })}
        </CardStack>

        <SaveBar>
          <CalendarInput
            nomarg
            width='220px'
            labelText='Effective from'
            minDate={new Date()}
            defaultValue={effectiveFrom}
            onChange={(d: Date | null) => d && setEffectiveFrom(d)}
          />
          <HelperText>Past coverage keeps the previous version.</HelperText>
          <Button variant='secondary' onClick={() => setHistoryOpen((v) => !v)}>
            {historyOpen ? 'Hide version history' : 'Version history'}
          </Button>
        </SaveBar>
        {/* TODO v2: "Suggest from planned work" — propose counts from shift-program bindings (workersPerLocation) and plan cycle parallelismTarget; manager approves. */}

        {historyOpen && (
          <VersionList>
            {sortedVersions.length ? (
              sortedVersions.map((v) => (
                <VersionRow key={v.id}>
                  <span>effective {v.effectiveFrom}</span>
                  <span>{v.shift.name}</span>
                  <span>
                    {v.isStaffed ? `${v.totalRequired} required` : 'staffing stopped'}
                    {v.totalRequiredChange !== 0
                      ? ` (${v.totalRequiredChange > 0 ? '+' : ''}${v.totalRequiredChange})`
                      : ''}
                  </span>
                  <span>by {v.createdBy ? `${v.createdBy.firstName} ${v.createdBy.lastName}`.trim() : '—'}</span>
                </VersionRow>
              ))
            ) : (
              <OtherText grey>No versions yet for this department and site.</OtherText>
            )}
          </VersionList>
        )}
      </PageWrapper>
    </>
  )
}
